import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  getAppRoutes,
  getRouteById,
  getRoutePath,
  resolveTenantNavigationItems,
  resolveTenantDefaultRoute,
  resolveTenantRoutes,
} from '@/app/router/route-registry';
import { defaultTenantId, resolveTenantConfig } from './tenant-config';
import { getStoredSessionUser } from '@/app/services/session.service';
import { hasRequiredRouteCapabilities } from '../app/router/access-policy';
import { getPlatformBranding, getTenantBranding } from './tenant-branding.service';

const TenantContext = createContext(null);

export function TenantProvider({ children }) {
  const [sessionTenantUser, setSessionTenantUser] = useState(() => getStoredSessionUser());
  const [dynamicBranding, setDynamicBranding] = useState({
    platform: null,
    tenant: null,
  });
  const sessionTenantId = sessionTenantUser?.tenantId || '';
  const resolvedTenantCandidate = sessionTenantId ? resolveTenantConfig(sessionTenantId) : null;
  const resolvedTenantConfig =
    resolvedTenantCandidate?.id === sessionTenantId ? resolvedTenantCandidate : null;
  const tenantConfig = useMemo(
    () => resolvedTenantConfig || resolveTenantConfig(defaultTenantId),
    [resolvedTenantConfig]
  );
  const runtimeTenant = useMemo(() => {
    const sessionTenant = sessionTenantUser?.tenant;

    if (!sessionTenantUser) {
      return tenantConfig;
    }

    return {
      ...tenantConfig,
      id: sessionTenantId || tenantConfig.id,
      name: sessionTenant?.name || tenantConfig.name,
      active: sessionTenant?.active !== false,
      slug: sessionTenant?.slug || tenantConfig.slug || '',
      planCode: sessionTenant?.planCode || tenantConfig.planCode || '',
      settings: sessionTenant?.settings || tenantConfig.settings || {},
    };
  }, [sessionTenantId, sessionTenantUser, tenantConfig]);
  const access = useMemo(() => {
    const staticAccess = tenantConfig.access || {};
    const runtimeCapabilities =
      sessionTenantUser?.capabilities && typeof sessionTenantUser.capabilities === 'object'
        ? sessionTenantUser.capabilities
        : null;

    if (!sessionTenantUser) {
      return staticAccess;
    }

    if (!runtimeCapabilities) {
      return {
        ...staticAccess,
        enabledModuleKeys: [],
        visibleRouteIds: [],
        capabilities: {},
        capabilityState: 'unavailable',
      };
    }

    if (runtimeCapabilities.canAccessWorkspace !== true) {
      return {
        ...staticAccess,
        enabledModuleKeys: [],
        visibleRouteIds: [],
        capabilities: runtimeCapabilities,
        capabilityState: 'blocked',
      };
    }

    const enabledModuleKeys = Array.isArray(runtimeCapabilities.enabledModuleKeys)
      ? runtimeCapabilities.enabledModuleKeys
      : [];
    const visibleRouteIds = (staticAccess.visibleRouteIds || []).filter((routeId) => {
      const route = getRouteById(routeId);

      if (!route) {
        return false;
      }

      const moduleEnabled = !route.moduleKey || enabledModuleKeys.includes(route.moduleKey);
      return moduleEnabled && hasRequiredRouteCapabilities(route, runtimeCapabilities);
    });

    return {
      ...staticAccess,
      enabledModuleKeys,
      visibleRouteIds,
      capabilities: runtimeCapabilities,
      capabilityState: 'ready',
    };
  }, [sessionTenantUser, tenantConfig]);
  const availableRoutes = useMemo(
    () => resolveTenantRoutes(getAppRoutes(), access),
    [access]
  );
  const navigation = useMemo(
    () => resolveTenantNavigationItems(tenantConfig.navigation?.items, access),
    [access, tenantConfig]
  );
  const defaultRoute = useMemo(
    () => resolveTenantDefaultRoute(tenantConfig, availableRoutes),
    [availableRoutes, tenantConfig]
  );
  const [themeMode, setThemeMode] = useState(() => {
    if (typeof window === 'undefined') return 'dark';
    return window.localStorage.getItem(tenantConfig.theme.modeStorageKey) || 'dark';
  });

  useEffect(() => {
    function syncSessionTenant() {
      setSessionTenantUser(getStoredSessionUser());
    }

    window.addEventListener('webtika:session-user-changed', syncSessionTenant);
    window.addEventListener('storage', syncSessionTenant);

    return () => {
      window.removeEventListener('webtika:session-user-changed', syncSessionTenant);
      window.removeEventListener('storage', syncSessionTenant);
    };
  }, []);

  useEffect(() => {
    let active = true;

    async function loadBranding() {
      const [platformResult, tenantResult] = await Promise.allSettled([
        getPlatformBranding(),
        sessionTenantUser ? getTenantBranding() : Promise.resolve(null),
      ]);

      if (!active) {
        return;
      }

      setDynamicBranding({
        platform: platformResult.status === 'fulfilled' ? platformResult.value : null,
        tenant: tenantResult.status === 'fulfilled' ? tenantResult.value : null,
      });
    }

    loadBranding();

    return () => {
      active = false;
    };
  }, [sessionTenantId, sessionTenantUser]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', themeMode === 'dark');
    window.localStorage.setItem(tenantConfig.theme.modeStorageKey, themeMode);
  }, [tenantConfig.theme.modeStorageKey, themeMode]);

  const resolvedBranding = useMemo(() => {
    const runtimeBranding = runtimeTenant?.settings?.branding || {};
    const tenantBranding = dynamicBranding.tenant || {};
    return {
      ...tenantConfig.branding,
      ...runtimeBranding,
      platformLogoUrl: dynamicBranding.platform?.platformLogoUrl || tenantConfig.branding.platformLogoUrl || tenantConfig.branding.appLogoUrl,
      platformMarkUrl: dynamicBranding.platform?.platformMarkUrl || tenantConfig.branding.platformMarkUrl,
      faviconUrl: dynamicBranding.platform?.faviconUrl || tenantConfig.branding.faviconUrl,
      clientName: tenantBranding.tenantName || runtimeBranding.clientName || runtimeTenant?.name || tenantConfig.branding.clientName,
      clientSubtitle: runtimeBranding.clientSubtitle || tenantConfig.branding.clientSubtitle || 'Area riservata',
      sidebarLogoUrl:
        tenantBranding.sidebarLogoUrl ||
        runtimeBranding.sidebarLogoUrl ||
        sessionTenantUser?.tenant?.settings?.branding?.sidebarLogoUrl ||
        tenantConfig.branding.sidebarLogoUrl,
      tenantTemplates: tenantBranding.templates || [],
    };
  }, [dynamicBranding, runtimeTenant, sessionTenantUser, tenantConfig]);

  const value = useMemo(
    () => ({
      tenant: runtimeTenant,
      tenantId: runtimeTenant.id,
      sessionTenant: sessionTenantUser?.tenant || null,
      sessionTenantId,
      hasResolvedTenantConfig: Boolean(resolvedTenantConfig),
      branding: resolvedBranding,
      theme: tenantConfig.theme,
      access,
      dashboard: tenantConfig.dashboard,
      navigation,
      navigationConfig: tenantConfig.navigation,
      hasAvailableRoutes: availableRoutes.length > 0,
      defaultRouteId: defaultRoute?.id || tenantConfig.defaultRouteId,
      defaultRoutePath: defaultRoute?.path || getRoutePath(tenantConfig.defaultRouteId),
      enabledRouteIds: access.visibleRouteIds || [],
      enabledModuleKeys: access.enabledModuleKeys || [],
      labels: tenantConfig.labels,
      themeMode,
      toggleTheme: () =>
        setThemeMode((previousMode) => (previousMode === 'dark' ? 'light' : 'dark')),
    }),
    [access, availableRoutes.length, defaultRoute, navigation, resolvedBranding, resolvedTenantConfig, runtimeTenant, sessionTenantId, sessionTenantUser, tenantConfig, themeMode]
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  const context = useContext(TenantContext);

  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }

  return context;
}
